import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  InputAdornment,
  SelectChangeEvent,
  Switch,
  FormHelperText,
  FormControlLabel
} from '@mui/material';
import { 
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Event as EventIcon,
  Link as LinkIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { Timestamp, collection, query, orderBy, limit, getDocs, where, addDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import eventbriteService, { NormalizedEvent } from '../../../services/eventbriteService';
import { mockEventbriteService } from '../../../services/mockEventbriteService';
import { createApiUrl } from '../../../utils/apiConfig';

type EventData = {
   id: string;
   title: string;
   description: string;
   startDate: Timestamp;
   endDate: Timestamp;
   location: {
      name: string;
      address: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
      latitude?: number;
      longitude?: number;
   };
   organizer: {
      name: string;
      email?: string;
      phone?: string;
   };
   category: string;
   tags: string[];
   sourceId: string;
   sourceName: string;
   sourceEventId: string;
   sourceUrl: string;
   price?: {
      currency: string;
      min: number;
      max: number;
   };
   capacity?: number;
   attendeeCount?: number;
   isOnline: boolean;
   status: 'active' | 'cancelled' | 'ended' | 'draft';
   createdAt: Timestamp;
   updatedAt: Timestamp;
   lastSyncedAt: Timestamp;
   imageUrl?: string;
};

const EventExplorer: React.FC = () => {
   const [events, setEvents] = useState<EventData[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [searchTerm, setSearchTerm] = useState('');
   const [filters, setFilters] = useState({
      category: '',
      source: '',
      status: '',
      startDate: null as Date | null,
      endDate: null as Date | null,
      isOnline: '',
   });
   const [page, setPage] = useState(0);
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
   const [openEventDialog, setOpenEventDialog] = useState(false);
   const [totalEvents, setTotalEvents] = useState(0);
   const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
   const [categories, setCategories] = useState<string[]>([]);
   const [apiConfigDialogOpen, setApiConfigDialogOpen] = useState(false);
   const [apiConfigValues, setApiConfigValues] = useState({
      apiKey: '',
      privateToken: '',
      publicToken: '',
   });
   const [useMockData, setUseMockData] = useState(true);

   // Fetch events
   useEffect(() => {
      // TODO: Initialize API token from Redux store when available
      console.warn('API token management moved to Redux. Using mock data for now.');
      setUseMockData(true);

      fetchEvents();
      fetchMetadata();
   }, []);

   const fetchMetadata = async () => {
      try {
         // Fetch data sources
         const sourcesData = [
            { id: 'eventbrite', name: 'Eventbrite' },
            { id: 'meetup', name: 'Meetup' },
            { id: 'facebook', name: 'Facebook Events' },
            { id: 'manual', name: 'Manual Entry' },
         ];
         setSources(sourcesData);

         // Fetch categories
         const categoriesData = [
            'Music',
            'Food & Drink',
            'Arts',
            'Business',
            'Science & Tech',
            'Sports & Fitness',
         ];
         setCategories(categoriesData);
      } catch (err) {
         console.error('Error fetching metadata:', err);
      }
   };

   const fetchEvents = async () => {
      try {
         setLoading(true);
         setError(null);

         // Determine if we should use real or mock data
         // TODO: Get token from Redux store instead
         const useRealApi = false; // Disabled until Redux integration

         if (useRealApi) {
            // Try to use the real Eventbrite API if we have credentials
            try {
               // TODO: Get token from Redux store
               console.log('Using Eventbrite API with Redux token (not implemented yet)');

               const params: any = {
                  q: searchTerm,
                  page: page + 1,
                  pageSize: rowsPerPage,
               };

               // Add filters if they are set
               if (filters.startDate || filters.endDate) {
                  // Handle date parameters properly
                  if (filters.startDate) {
                     params.startDate = filters.startDate;
                  }

                  if (filters.endDate) {
                     params.endDate = filters.endDate;
                  }
               }

               if (filters.category) params.city = filters.category; // Using category field for city temporarily
               if (filters.isOnline === 'online') params.onlineEvents = true;
               if (filters.isOnline === 'inperson') params.onlineEvents = false;

               console.log('Sending API request with params:', params);

               const response = await eventbriteService.searchEvents(params);

               console.log('API response:', response);

               const transformedEvents = response.events.map(
                  (event: NormalizedEvent): EventData => ({
                     id: event.id,
                     title: event.title,
                     description: event.description,
                     startDate: Timestamp.fromDate(new Date(event.startDate)),
                     endDate: Timestamp.fromDate(new Date(event.endDate)),
                     location: {
                        name: event.venue || '',
                        address: event.location.address || '',
                        city: event.location.city || '',
                        state: event.location.state || '',
                        postalCode: event.location.postalCode || '',
                        country: event.location.country || '',
                        latitude: event.location.latitude
                           ? parseFloat(event.location.latitude)
                           : undefined,
                        longitude: event.location.longitude
                           ? parseFloat(event.location.longitude)
                           : undefined,
                     },
                     organizer: {
                        name: event.organizerName || 'Unknown Organizer',
                     },
                     category: event.categories?.[0] || '',
                     tags: event.tags || [],
                     sourceId: event.sourceId,
                     sourceName: 'eventbrite',
                     sourceEventId: event.sourceId,
                     sourceUrl: event.url,
                     isOnline: event.isOnline,
                     status: 'active',
                     createdAt: Timestamp.now(),
                     updatedAt: Timestamp.now(),
                     lastSyncedAt: Timestamp.now(),
                     imageUrl: event.imageUrl,
                  })
               );

               setEvents(transformedEvents);
               setTotalEvents(response.pagination?.object_count || transformedEvents.length);
               setLoading(false);
               return;
            } catch (apiError: any) {
               console.error('Error using Eventbrite API:', apiError);

               // Show more detailed error messages
               let errorMsg = 'Error fetching events from Eventbrite API. ';

               if (apiError.response) {
                  // The request was made and the server responded with a non-2xx status code
                  errorMsg += `Server responded with status code ${apiError.response.status}: ${apiError.response.data?.error_description || 'Unknown error'}.`;
               } else if (apiError.request) {
                  // The request was made but no response was received
                  errorMsg +=
                     'No response received from server. Please check your internet connection.';
               } else {
                  // Something happened in setting up the request
                  errorMsg += apiError.message || 'Unknown error occurred';
               }

               // TODO: Validate token from Redux store
               errorMsg += ' Please check your API token configuration.';

               setError(errorMsg + ' Using reliable mock data instead.');

               // Fall back to mock data if API call fails
               setUseMockData(true);
            }
         }

         // Use mock data (either by choice or as fallback)
         // TODO: Check Redux store for token instead
         if (useMockData) {
            console.log('Using mock event data');

            try {
               // Use our mock service to get consistent data
               const mockParams = {
                  q: searchTerm,
                  city: filters.category, // Using category field for city temporarily
                  startDate: filters.startDate,
                  endDate: filters.endDate,
                  onlineEvents:
                     filters.isOnline === 'online'
                        ? true
                        : filters.isOnline === 'inperson'
                          ? false
                          : undefined,
                  page: page + 1,
                  pageSize: rowsPerPage,
               };

               const response = await mockEventbriteService.searchEvents(mockParams);

               // Convert from NormalizedEvent to EventData
               const transformedEvents = response.events.map(
                  (event: NormalizedEvent): EventData => ({
                     id: event.id,
                     title: event.title,
                     description: event.description,
                     startDate: Timestamp.fromDate(new Date(event.startDate)),
                     endDate: Timestamp.fromDate(new Date(event.endDate)),
                     location: {
                        name: event.venue || '',
                        address: event.location.address || '',
                        city: event.location.city || '',
                        state: event.location.state || '',
                        postalCode: event.location.postalCode || '',
                        country: event.location.country || '',
                        latitude: event.location.latitude
                           ? parseFloat(event.location.latitude)
                           : undefined,
                        longitude: event.location.longitude
                           ? parseFloat(event.location.longitude)
                           : undefined,
                     },
                     organizer: {
                        name: event.organizerName || 'Unknown Organizer',
                     },
                     category: event.categories?.[0] || '',
                     tags: event.tags || [],
                     sourceId: event.sourceId,
                     sourceName: 'eventbrite',
                     sourceEventId: event.sourceId,
                     sourceUrl: event.url,
                     isOnline: event.isOnline,
                     status: 'active',
                     createdAt: Timestamp.now(),
                     updatedAt: Timestamp.now(),
                     lastSyncedAt: Timestamp.now(),
                     imageUrl: event.imageUrl,
                  })
               );

               setEvents(transformedEvents);
               setTotalEvents(response.pagination.object_count);
               setLoading(false);

               // If we were forced into mock mode due to an error, show a warning
               if (!useMockData) {
                  setError(
                     'Unable to connect to Eventbrite API. Using reliable mock data instead.'
                  );
               }

               return;
            } catch (mockError) {
               console.error('Error with mock data:', mockError);
               setError('An unexpected error occurred with the mock data service.');
            }
         }

         setLoading(false);
      } catch (err) {
         console.error('Error fetching events:', err);
         setError('Failed to fetch events. Please try again.');
         setLoading(false);
      }
   };

   const applyFilters = (events: EventData[]) => {
      return events.filter((event) => {
         // Search term
         if (
            searchTerm &&
            !event.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !event.description.toLowerCase().includes(searchTerm.toLowerCase())
         ) {
            return false;
         }

         // Category filter
         if (filters.category && event.category !== filters.category) {
            return false;
         }

         // Source filter
         if (filters.source && event.sourceId !== filters.source) {
            return false;
         }

         // Status filter
         if (filters.status && event.status !== filters.status) {
            return false;
         }

         // Start date filter
         if (filters.startDate && new Date(event.startDate.seconds * 1000) < filters.startDate) {
            return false;
         }

         // End date filter
         if (filters.endDate && new Date(event.startDate.seconds * 1000) > filters.endDate) {
            return false;
         }

         // Online filter
         if (filters.isOnline === 'online' && !event.isOnline) {
            return false;
         } else if (filters.isOnline === 'inperson' && event.isOnline) {
            return false;
         }

         return true;
      });
   };

   const handleSearch = () => {
      fetchEvents();
   };

   const handleFilterChange = (name: string, value: any) => {
      setFilters((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const resetFilters = () => {
      setSearchTerm('');
      setFilters({
         category: '',
         source: '',
         status: '',
         startDate: null,
         endDate: null,
         isOnline: '',
      });
   };

   const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
      fetchEvents();
   };

   const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
      fetchEvents();
   };

   const handleOpenEventDetails = (event: EventData) => {
      setSelectedEvent(event);
      setOpenEventDialog(true);
   };

   const handleCloseEventDialog = () => {
      setOpenEventDialog(false);
   };

   const handleApiConfigOpen = () => {
      // TODO: Get token from Redux store
      setApiConfigValues({
         apiKey: '',
         privateToken: '', // TODO: Get from Redux
         publicToken: '',
      });
      setApiConfigDialogOpen(true);
   };

   const handleApiConfigClose = () => {
      setApiConfigDialogOpen(false);
   };

   const handleApiConfigSave = () => {
      // Update the token in the service
      eventbriteService.setApiToken(apiConfigValues.privateToken);

      // TODO: Store the token in Redux store instead of localStorage
      console.log('Token saved to service. Redux integration needed for persistence.');

      setApiConfigDialogOpen(false);
      fetchEvents(); // Refresh with new API settings
   };

   const handleApiConfigChange = (field: string, value: string) => {
      setApiConfigValues((prev) => ({
         ...prev,
         [field]: value,
      }));
   };

   const renderEventDialog = () => {
      if (!selectedEvent) return null;

      return (
         <Dialog open={openEventDialog} onClose={handleCloseEventDialog} maxWidth='md' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='h6'>{selectedEvent.title}</Typography>
                  <Chip
                     label={selectedEvent.status}
                     color={
                        selectedEvent.status === 'active'
                           ? 'success'
                           : selectedEvent.status === 'cancelled'
                             ? 'error'
                             : selectedEvent.status === 'ended'
                               ? 'default'
                               : 'warning'
                     }
                     size='small'
                  />
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               <Grid container spacing={3}>
                  {/* Event image */}
                  {selectedEvent.imageUrl && (
                     <Grid item xs={12} md={4}>
                        <img
                           src={selectedEvent.imageUrl}
                           alt={selectedEvent.title}
                           style={{
                              width: '100%',
                              maxHeight: '200px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                           }}
                        />
                     </Grid>
                  )}

                  {/* Event details */}
                  <Grid item xs={12} md={selectedEvent.imageUrl ? 8 : 12}>
                     <Typography variant='body1' paragraph>
                        {selectedEvent.description}
                     </Typography>

                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <Typography variant='subtitle2' color='primary'>
                              Date & Time
                           </Typography>
                           <Typography variant='body2'>
                              Start:{' '}
                              {format(new Date(selectedEvent.startDate.seconds * 1000), 'PPp')}
                           </Typography>
                           <Typography variant='body2'>
                              End: {format(new Date(selectedEvent.endDate.seconds * 1000), 'PPp')}
                           </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                           <Typography variant='subtitle2' color='primary'>
                              Location
                           </Typography>
                           {selectedEvent.isOnline ? (
                              <Typography variant='body2'>Online Event</Typography>
                           ) : (
                              <>
                                 <Typography variant='body2'>
                                    {selectedEvent.location.name}
                                 </Typography>
                                 <Typography variant='body2'>
                                    {selectedEvent.location.address}, {selectedEvent.location.city},{' '}
                                    {selectedEvent.location.state}{' '}
                                    {selectedEvent.location.postalCode}
                                 </Typography>
                                 <Typography variant='body2'>
                                    {selectedEvent.location.country}
                                 </Typography>
                              </>
                           )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                           <Typography variant='subtitle2' color='primary'>
                              Organizer
                           </Typography>
                           <Typography variant='body2'>{selectedEvent.organizer.name}</Typography>
                           {selectedEvent.organizer.email && (
                              <Typography variant='body2'>
                                 {selectedEvent.organizer.email}
                              </Typography>
                           )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                           <Typography variant='subtitle2' color='primary'>
                              Event Details
                           </Typography>
                           <Typography variant='body2'>
                              Category: {selectedEvent.category}
                           </Typography>
                           {selectedEvent.tags && selectedEvent.tags.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                 {selectedEvent.tags.map((tag) => (
                                    <Chip key={tag} label={tag} size='small' />
                                 ))}
                              </Box>
                           )}
                        </Grid>

                        {selectedEvent.price && (
                           <Grid item xs={12} sm={6}>
                              <Typography variant='subtitle2' color='primary'>
                                 Price
                              </Typography>
                              <Typography variant='body2'>
                                 {selectedEvent.price.min === selectedEvent.price.max
                                    ? `${selectedEvent.price.currency} ${selectedEvent.price.min}`
                                    : `${selectedEvent.price.currency} ${selectedEvent.price.min} - ${selectedEvent.price.max}`}
                              </Typography>
                           </Grid>
                        )}

                        {(selectedEvent.capacity || selectedEvent.attendeeCount) && (
                           <Grid item xs={12} sm={6}>
                              <Typography variant='subtitle2' color='primary'>
                                 Attendance
                              </Typography>
                              {selectedEvent.capacity && (
                                 <Typography variant='body2'>
                                    Capacity: {selectedEvent.capacity}
                                 </Typography>
                              )}
                              {selectedEvent.attendeeCount && (
                                 <Typography variant='body2'>
                                    Attendees: {selectedEvent.attendeeCount}
                                 </Typography>
                              )}
                           </Grid>
                        )}
                     </Grid>
                  </Grid>

                  <Grid item xs={12}>
                     <Divider sx={{ my: 2 }} />
                     <Typography variant='subtitle2' color='primary'>
                        Source Information
                     </Typography>
                     <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                           <Typography variant='body2'>
                              Source: {selectedEvent.sourceName}
                           </Typography>
                           <Typography variant='body2'>
                              Source ID: {selectedEvent.sourceEventId}
                           </Typography>
                           <Typography
                              variant='body2'
                              sx={{ display: 'flex', alignItems: 'center' }}
                           >
                              Source URL:
                              <IconButton
                                 size='small'
                                 href={selectedEvent.sourceUrl}
                                 target='_blank'
                                 rel='noopener noreferrer'
                              >
                                 <OpenInNewIcon fontSize='small' />
                              </IconButton>
                           </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <Typography variant='body2'>
                              Created:{' '}
                              {format(new Date(selectedEvent.createdAt.seconds * 1000), 'PPp')}
                           </Typography>
                           <Typography variant='body2'>
                              Updated:{' '}
                              {format(new Date(selectedEvent.updatedAt.seconds * 1000), 'PPp')}
                           </Typography>
                           <Typography variant='body2'>
                              Last Synced:{' '}
                              {format(new Date(selectedEvent.lastSyncedAt.seconds * 1000), 'PPp')}
                           </Typography>
                        </Grid>
                     </Grid>
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button
                  href={selectedEvent.sourceUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  startIcon={<OpenInNewIcon />}
               >
                  View Original
               </Button>
               <Button onClick={handleCloseEventDialog}>Close</Button>
            </DialogActions>
         </Dialog>
      );
   };

   const renderApiConfigDialog = () => {
      return (
         <Dialog open={apiConfigDialogOpen} onClose={handleApiConfigClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ bgcolor: '#FBBC05', color: 'white' }}>
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  Eventbrite API Configuration
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               <Alert severity='info' sx={{ mb: 3 }}>
                  Enter your Eventbrite API credentials below to fetch real events. Your credentials
                  are stored securely in your browser's local storage.
               </Alert>

               <FormControl component='fieldset' sx={{ mb: 3 }}>
                  <FormControlLabel
                     control={
                        <Switch
                           checked={!useMockData}
                           onChange={(e) => setUseMockData(!e.target.checked)}
                           color='primary'
                        />
                     }
                     label='Use real Eventbrite API'
                  />
                  <FormHelperText>
                     {useMockData
                        ? 'Currently using reliable mock data for development'
                        : 'Currently using real Eventbrite API (requires valid credentials)'}
                  </FormHelperText>
               </FormControl>

               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <TextField
                        label='API Key'
                        value={apiConfigValues.apiKey}
                        onChange={(e) => handleApiConfigChange('apiKey', e.target.value)}
                        fullWidth
                        margin='normal'
                        helperText='Your Eventbrite API Key (e.g., 5FCKDEHSCRIIR5LKLB)'
                        disabled={useMockData}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <TextField
                        label='Private Token'
                        value={apiConfigValues.privateToken}
                        onChange={(e) => handleApiConfigChange('privateToken', e.target.value)}
                        fullWidth
                        margin='normal'
                        helperText='Your Eventbrite Private Token (used for API requests)'
                        disabled={useMockData}
                     />
                  </Grid>
                  <Grid item xs={12}>
                     <TextField
                        label='Public Token'
                        value={apiConfigValues.publicToken}
                        onChange={(e) => handleApiConfigChange('publicToken', e.target.value)}
                        fullWidth
                        margin='normal'
                        helperText='Your Eventbrite Public Token'
                        disabled={useMockData}
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
               <Button onClick={handleApiConfigClose} variant='outlined'>
                  Cancel
               </Button>
               <Button
                  onClick={handleApiConfigSave}
                  variant='contained'
                  color='primary'
                  startIcon={<SaveIcon />}
               >
                  Save Configuration
               </Button>
            </DialogActions>
         </Dialog>
      );
   };

   if (loading && events.length === 0) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
         </Box>
      );
   }

   return (
      <Box>
         <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
         >
            <Typography variant='h5' component='h2'>
               Event Explorer
            </Typography>

            <Box>
               <Button
                  variant='outlined'
                  color='secondary'
                  startIcon={<SettingsIcon />}
                  onClick={handleApiConfigOpen}
                  sx={{ mr: 1 }}
               >
                  API Config
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<RefreshIcon />}
                  onClick={fetchEvents}
               >
                  Refresh Events
               </Button>
            </Box>
         </Box>

         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         {/* Search and Filter Section */}
         <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems='center'>
               <Grid item xs={12} md={4}>
                  <TextField
                     fullWidth
                     label='Search Events'
                     variant='outlined'
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     placeholder='Search by title or description'
                     InputProps={{
                        startAdornment: <SearchIcon color='action' sx={{ mr: 1 }} />,
                     }}
                  />
               </Grid>

               <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                     <InputLabel>Category</InputLabel>
                     <Select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        label='Category'
                     >
                        <MenuItem value=''>All</MenuItem>
                        {categories.map((category) => (
                           <MenuItem key={category} value={category}>
                              {category}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                     <InputLabel>Source</InputLabel>
                     <Select
                        value={filters.source}
                        onChange={(e) => handleFilterChange('source', e.target.value)}
                        label='Source'
                     >
                        <MenuItem value=''>All</MenuItem>
                        {sources.map((source) => (
                           <MenuItem key={source.id} value={source.id}>
                              {source.name}
                           </MenuItem>
                        ))}
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                     <InputLabel>Status</InputLabel>
                     <Select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        label='Status'
                     >
                        <MenuItem value=''>All</MenuItem>
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='ended'>Ended</MenuItem>
                        <MenuItem value='cancelled'>Cancelled</MenuItem>
                        <MenuItem value='draft'>Draft</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={6} md={2}>
                  <FormControl fullWidth>
                     <InputLabel>Event Type</InputLabel>
                     <Select
                        value={filters.isOnline}
                        onChange={(e) => handleFilterChange('isOnline', e.target.value)}
                        label='Event Type'
                     >
                        <MenuItem value=''>All</MenuItem>
                        <MenuItem value='online'>Online</MenuItem>
                        <MenuItem value='inperson'>In-Person</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>

               <Grid item xs={6} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <DatePicker
                        label='From Date'
                        value={filters.startDate}
                        onChange={(newValue) => handleFilterChange('startDate', newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size='small' />}
                     />
                  </LocalizationProvider>
               </Grid>

               <Grid item xs={6} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <DatePicker
                        label='To Date'
                        value={filters.endDate}
                        onChange={(newValue) => handleFilterChange('endDate', newValue)}
                        renderInput={(params) => <TextField {...params} fullWidth size='small' />}
                     />
                  </LocalizationProvider>
               </Grid>

               <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 1 }}>
                  <Button
                     variant='contained'
                     color='primary'
                     startIcon={<SearchIcon />}
                     onClick={handleSearch}
                  >
                     Search
                  </Button>
                  <Button variant='outlined' onClick={resetFilters}>
                     Reset Filters
                  </Button>
               </Grid>
            </Grid>
         </Paper>

         {/* Results Table */}
         <Paper>
            <TableContainer>
               <Table sx={{ minWidth: 750 }}>
                  <TableHead>
                     <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Date & Time</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align='right'>Actions</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {loading ? (
                        <TableRow>
                           <TableCell colSpan={6} align='center'>
                              <CircularProgress size={24} sx={{ my: 2 }} />
                           </TableCell>
                        </TableRow>
                     ) : events.length === 0 ? (
                        <TableRow>
                           <TableCell colSpan={6} align='center'>
                              No events found matching your criteria
                           </TableCell>
                        </TableRow>
                     ) : (
                        events.map((event) => (
                           <TableRow
                              key={event.id}
                              hover
                              onClick={() => handleOpenEventDetails(event)}
                              style={{ cursor: 'pointer' }}
                           >
                              <TableCell>
                                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {event.imageUrl ? (
                                       <img
                                          src={event.imageUrl}
                                          alt={event.title}
                                          style={{
                                             width: 40,
                                             height: 40,
                                             borderRadius: 4,
                                             marginRight: 8,
                                             objectFit: 'cover',
                                          }}
                                       />
                                    ) : (
                                       <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    )}
                                    <Typography variant='body2'>{event.title}</Typography>
                                 </Box>
                              </TableCell>
                              <TableCell>
                                 {format(new Date(event.startDate.seconds * 1000), 'PP')}
                                 <br />
                                 <Typography variant='caption' color='text.secondary'>
                                    {format(new Date(event.startDate.seconds * 1000), 'p')} -{' '}
                                    {format(new Date(event.endDate.seconds * 1000), 'p')}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 {event.isOnline ? (
                                    <Chip size='small' label='Online' color='info' />
                                 ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                       <LocationIcon
                                          fontSize='small'
                                          sx={{ mt: 0.5, mr: 0.5, color: 'error.main' }}
                                       />
                                       <Typography variant='body2'>
                                          {event.location.city}, {event.location.state}
                                       </Typography>
                                    </Box>
                                 )}
                              </TableCell>
                              <TableCell>
                                 <Chip size='small' label={event.sourceName} />
                              </TableCell>
                              <TableCell>
                                 <Chip
                                    size='small'
                                    label={event.status}
                                    color={
                                       event.status === 'active'
                                          ? 'success'
                                          : event.status === 'cancelled'
                                            ? 'error'
                                            : event.status === 'ended'
                                              ? 'default'
                                              : 'warning'
                                    }
                                 />
                              </TableCell>
                              <TableCell align='right'>
                                 <Tooltip title='View Details'>
                                    <IconButton
                                       size='small'
                                       onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenEventDetails(event);
                                       }}
                                    >
                                       <OpenInNewIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                                 <Tooltip title='View Source'>
                                    <IconButton
                                       size='small'
                                       href={event.sourceUrl}
                                       target='_blank'
                                       rel='noopener noreferrer'
                                       onClick={(e) => e.stopPropagation()}
                                    >
                                       <LinkIcon fontSize='small' />
                                    </IconButton>
                                 </Tooltip>
                              </TableCell>
                           </TableRow>
                        ))
                     )}
                  </TableBody>
               </Table>
            </TableContainer>
            <TablePagination
               rowsPerPageOptions={[10, 25, 50]}
               component='div'
               count={totalEvents}
               rowsPerPage={rowsPerPage}
               page={page}
               onPageChange={handleChangePage}
               onRowsPerPageChange={handleChangeRowsPerPage}
            />
         </Paper>

         {/* Event Details Dialog */}
         {renderEventDialog()}

         {/* API Configuration Dialog */}
         {renderApiConfigDialog()}
      </Box>
   );
};

const mockEvents: EventData[] = [
  {
    id: 'test1',
    title: 'Test Event 1',
    description: 'This is a test event',
    startDate: Timestamp.fromDate(new Date('2025-05-20T10:00:00')),
    endDate: Timestamp.fromDate(new Date('2025-05-20T15:00:00')),
    location: {
      name: 'Online',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    },
    organizer: {
      name: 'Test Organizer',
    },
    category: 'Test',
    tags: ['test', 'mock'],
    sourceId: 'eventbrite-123',
    sourceName: 'eventbrite',
    sourceEventId: '123',
    sourceUrl: 'https://example.com',
    isOnline: true,
    status: 'active',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastSyncedAt: Timestamp.now(),
  },
  {
    id: 'test2',
    title: 'Test Event 2',
    description: 'This is another test event',
    startDate: Timestamp.fromDate(new Date('2025-05-14T12:00:00')),
    endDate: Timestamp.fromDate(new Date('2025-05-14T15:30:00')),
    location: {
      name: 'Test Venue',
      address: '123 Main St',
      city: 'Chicago',
      state: 'CA',
      postalCode: '12345',
      country: 'USA',
    },
    organizer: {
      name: 'Test Organizer',
    },
    category: 'Meetup',
    tags: ['test', 'mock'],
    sourceId: 'meetup-456',
    sourceName: 'meetup',
    sourceEventId: '456',
    sourceUrl: 'https://example.com',
    isOnline: false,
    status: 'active',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastSyncedAt: Timestamp.now(),
  },
  {
    id: 'test3',
    title: 'Test Event 3',
    description: 'This is a third test event',
    startDate: Timestamp.fromDate(new Date('2025-05-15T13:30:00')),
    endDate: Timestamp.fromDate(new Date('2025-05-15T18:15:00')),
    location: {
      name: 'Another Venue',
      address: '456 Oak St',
      city: 'Chicago',
      state: 'CA',
      postalCode: '54321',
      country: 'USA',
    },
    organizer: {
      name: 'Test Organizer',
    },
    category: 'Facebook Events',
    tags: ['test', 'mock'],
    sourceId: 'facebook-789',
    sourceName: 'facebook',
    sourceEventId: '789',
    sourceUrl: 'https://example.com',
    isOnline: false,
    status: 'cancelled',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastSyncedAt: Timestamp.now(),
  },
  {
    id: 'test4',
    title: 'Test Event 4',
    description: 'This is a fourth test event',
    startDate: Timestamp.fromDate(new Date('2025-05-24T15:30:00')),
    endDate: Timestamp.fromDate(new Date('2025-05-24T19:30:00')),
    location: {
      name: 'Manual Venue',
      address: '789 Pine St',
      city: 'Los Angeles',
      state: 'CA',
      postalCode: '90001',
      country: 'USA',
    },
    organizer: {
      name: 'Test Organizer',
    },
    category: 'Manual Entry',
    tags: ['test', 'mock'],
    sourceId: 'manual-012',
    sourceName: 'manual',
    sourceEventId: '012',
    sourceUrl: 'https://example.com',
    isOnline: false,
    status: 'active',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    lastSyncedAt: Timestamp.now(),
  }
];

export default EventExplorer;
